import { Flatfile } from "@flatfile/api";

import { PAYABLES_SHEET_NAME, PAYABLES_SHEET_SLUG } from "../../constants";

/**
 * The spreadsheet contract users are shown on the start screen
 * (`PayableColumnsStackedView`), expressed as a Flatfile blueprint.
 *
 * v1 built this workbook server-side, in the Flatfile listener that pushed rows
 * into the v1 API. v3 has no Flatfile integration at all — the browser parses
 * the sheet and posts each row to `POST /v3/platform/batches/{id}/items` — so
 * the blueprint lives here.
 *
 * Line-item custom fields are deliberately absent: `PayableImportBatchItemData`
 * has no slot for them, so offering the columns would collect values the
 * importer then discards — and a required custom field would block submission
 * on data that never arrives. `allowAdditionalFields` still lets the user's
 * extra columns through the portal without being mapped.
 */
export const buildPayableImportSheetConfig = (): Flatfile.SheetConfig => ({
  name: PAYABLES_SHEET_NAME,
  slug: PAYABLES_SHEET_SLUG,
  allowAdditionalFields: true,
  fields: [
    {
      key: "email",
      label: "Contractor email",
      type: "string",
      description:
        "Email of an existing contractor. Provide this or an external ID."
    },
    {
      key: "contractorId",
      label: "Contractor external ID",
      type: "string",
      description:
        "Your own ID for the contractor. Provide this or an email address."
    },
    {
      key: "amount",
      label: "Amount",
      type: "number",
      constraints: [{ type: "required" }]
    },
    {
      key: "lineItemTitle",
      label: "Line item title",
      type: "string",
      constraints: [{ type: "required" }]
    },
    {
      key: "dueDate",
      label: "Due date",
      type: "date",
      constraints: [{ type: "required" }]
    },
    { key: "paidDate", label: "Pay date", type: "date" },
    {
      key: "lineItemDescription",
      label: "Line item description",
      type: "string"
    },
    { key: "reimbursable", label: "Reimbursable", type: "boolean" },
    { key: "invoiceNotes", label: "Invoice notes", type: "string" },
    { key: "attachmentId", label: "Attachment ID", type: "string" },
    {
      key: "paymentRefId",
      label: "Payment reference ID",
      type: "string",
      description:
        "Your own reference for this line item. Used to match repeat imports of the same row."
    }
  ]
});
